import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, HeadContent, Outlet } from "@tanstack/react-router";
import { CookieBanner } from "@/components/consent/cookie-banner";
import { Button } from "@/components/ui/button";
import { ButtonLink } from "@/components/ui/button-link";
import { rootHead } from "@/features/marketing/seo";
import { t } from "@/i18n";
import { errorMessage } from "@/lib/error-reporting";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Site-wide defaults (title, description, Open Graph image); every public route overrides what it needs (seo.ts).
  head: rootHead,
  component: () => (
    <>
      <HeadContent />
      <Outlet />
      <CookieBanner />
    </>
  ),
  notFoundComponent: () => (
    <div className="grid min-h-dvh place-items-center p-6 text-center">
      {/* The web Worker answers unknown paths 404 + noindex (worker/pages.ts); the tag covers `vite dev` and the service worker's shell. */}
      <meta name="robots" content="noindex" />
      <div>
        <img src="/logo.png" alt="" className="mx-auto mb-4 h-16 w-16 rounded-2xl" />
        <h1 className="font-display text-2xl font-bold">{t("common.notFoundTitle")}</h1>
        <p className="mt-1 text-text-secondary">{t("common.notFoundBody")}</p>
        <ButtonLink to="/" className="mt-4">{t("common.goHome")}</ButtonLink>
      </div>
    </div>
  ),
  // The router reports the error itself (`defaultOnCatch` in main.tsx); a server error shows its request id to quote.
  errorComponent: ({ error, reset }) => (
    <div className="grid min-h-dvh place-items-center p-6 text-center">
      <div className="max-w-md">
        <h1 className="font-display text-2xl font-bold">{t("common.somethingWrong")}</h1>
        <p className="mt-1 break-words text-sm text-text-secondary">{errorMessage(error)}</p>
        <Button className="mt-4" onClick={reset}>{t("common.retry")}</Button>
      </div>
    </div>
  ),
});
